import { Eye, EyeSlash } from "iconsax-react";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

export default function PasswordChangePage() {
    const [formData, setFormData] = useState({
        newPassword: "",
        confirmPassword: ""
    });
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();

    // test effect
    const [logo, setLogo] = useState(null);

    useEffect(() => {
        const testLogo = window.localStorage.getItem("PHARM_LOGO");
        if (!testLogo) {
            console.log("There is no logo use default");
            setLogo("/clinic/images/vitalrx-logo.png");
        } else {
            console.log("there is logo");
            setLogo("/clinic/images/vitalrx-logo.png");
        }
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMessage("");

        // Basic validation
        if (!formData.newPassword || !formData.confirmPassword) {
            setErrorMessage("Please fill in all fields");
            setLoading(false);
            return;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            setErrorMessage("Passwords do not match");
            setLoading(false);
            return;
        }

        if (formData.newPassword.length < 8) {
            setErrorMessage("Password must be at least 8 characters long");
            setLoading(false);
            return;
        }

        // Placeholder for form submission
        setTimeout(() => {
            setSuccess(true);
            setLoading(false);
        }, 2000);
    };

    return (
        <div className="min-h-screen flex">
            {/* Left Section - Password Change Form */}
            <div className="flex-1 bg-white flex flex-col px-8 lg:px-16 xl:px-24">
                <div className="max-w-md mx-auto w-full py-8 flex flex-col justify-center min-h-screen">
                    {/* Logo */}
                    <div className="mb-8">
                        <img src={logo} className="w-32 h-auto" alt="Pharmacy logo" />
                    </div>

                    {/* Main Content */}
                    {success ? (
                        <div className="text-gray-600 text-center">
                            <div className="text-lg font-medium mb-2">Password updated successfully</div>
                            <div className="text-sm mb-6">
                                Your password has been changed successfully. You can now sign in with your new password.
                            </div>
                            <Link
                                to="/auth/login"
                                className="inline-block bg-[#007b7d] text-white py-3 px-6 rounded-lg font-medium hover:bg-[#005a5c] transition-colors"
                            >
                                Sign in
                            </Link>
                        </div>
                    ) : (
                        <>
                            <div className="mb-8">
                                {/* <h1 className="text-2xl font-bold text-gray-900 mb-2">
                                    Set new password
                                </h1> */}
                                <p className="text-gray-600 text-sm">
                                    Enter your new password below. Make sure it's secure and easy for you to remember.
                                </p>
                            </div>

                            {/* Password Change Form */}
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* New Password Field */}
                                <div>
                                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                                        New password
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                            </svg>
                                        </div>
                                        <input
                                            id="newPassword"
                                            name="newPassword"
                                            type={showNewPassword ? "text" : "password"}
                                            value={formData.newPassword}
                                            onChange={handleInputChange}
                                            className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007b7d] focus:border-transparent outline-none transition-colors"
                                            placeholder="Enter new password"
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                        >
                                            {showNewPassword ? (
                                                <EyeSlash size={16} className="text-gray-400 hover:text-gray-600" />
                                            ) : (
                                                <Eye size={16} className="text-gray-400 hover:text-gray-600" />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* Confirm Password Field */}
                                <div>
                                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                                        Confirm new password
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                            </svg>
                                        </div>
                                        <input
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            type={showConfirmPassword ? "text" : "password"}
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange}
                                            className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#007b7d] focus:border-transparent outline-none transition-colors"
                                            placeholder="Confirm new password"
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        >
                                            {showConfirmPassword ? (
                                                <EyeSlash size={16} className="text-gray-400 hover:text-gray-600" />
                                            ) : (
                                                <Eye size={16} className="text-gray-400 hover:text-gray-600" />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* Password Requirements */}
                                <div className="text-xs text-gray-500 space-y-1">
                                    <div>Password requirements:</div>
                                    <div>• At least 8 characters long</div>
                                    <div>• Must match in both fields</div>
                                </div>

                                {/* Error Message */}
                                {errorMessage && (
                                    <div className="text-red-500 text-sm font-medium">
                                        {errorMessage}
                                    </div>
                                )}

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={loading || !formData.newPassword || !formData.confirmPassword}
                                    className="w-full bg-[#007b7d] text-white py-3 px-4 rounded-lg font-medium hover:bg-[#005a5c] focus:ring-2 focus:ring-[#007b7d] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {loading ? "Updating password..." : "Update password"}
                                </button>
                            </form>

                            {/* Links */}
                            <div className="mt-6 space-y-3 text-center">
                                <div className="text-sm text-gray-600">
                                    Remember your password?{" "}
                                    <Link
                                        to="/auth/login"
                                        className="font-medium text-gray-900 hover:text-gray-700"
                                    >
                                        Sign in
                                    </Link>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Right Section - Background Image */}
            <div style={{ backgroundImage: "url('/clinic/images/authimg2.jpg')" }} className="hidden lg:flex lg:flex-1 relative bg-center bg-cover bg-no-repeat">
        <div className="absolute inset-0 bg-gradient-to-br from-[#007b7d]/70 via-[#005a5c]/60 to-black/80">
          {/* Abstract shapes overlay */}
          <div className="absolute inset-0">
            <div className="absolute top-20 left-10 w-32 h-32 bg-[#007b7d]/20 rounded-full blur-xl"></div>
            <div className="absolute top-40 right-20 w-24 h-24 bg-[#007b7d]/15 rounded-full blur-xl"></div>
            <div className="absolute bottom-40 left-20 w-40 h-40 bg-[#005a5c]/25 rounded-full blur-xl"></div>
            <div className="absolute bottom-20 right-10 w-28 h-28 bg-[#007b7d]/10 rounded-full blur-xl"></div>
          </div>

          {/* Mountain silhouette */}
          <div className="absolute bottom-0 right-0 w-full h-32">
            <svg viewBox="0 0 400 100" className="w-full h-full">
              <path d="M0,100 L50,60 L100,80 L150,40 L200,70 L250,30 L300,50 L350,20 L400,40 L400,100 Z"
                fill="rgba(0,123,125,0.4)" />
            </svg>
          </div>

        
        </div>
      </div>
      {/* Powered by */}
          <div className="absolute bottom-4 left-4 flex items-center gap-2 text-[#111111] text-xs">
            <span>Powered by</span>
            <img src="/clinic/images/logo-bw.png" className="w-24 h-auto" alt="BCRX" />
          </div>
        </div>
    );
}
